/**
 * GlobalSearchCommand Component
 *
 * グローバル検索機能 (Cmd+K / Ctrl+K)
 * shadcn/ui CommandDialogを使用した統合検索UI
 *
 * Features:
 * - Cmd+K / Ctrl+K でトグル
 * - ナビゲーションショートカット (⌘D, ⌘R, ⌘C等)
 * - リアルタイム検索 (顧客、予約)
 * - AI相談統合
 *
 * Design System v3.0準拠
 */

import * as React from 'react';
import {
  Calendar,
  Settings,
  Users,
  UserCog,
  LayoutDashboard,
  TrendingUp,
  Sparkles,
  Building2,
  HelpCircle,
  HeartHandshake,
  MessageCircle,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { useCustomers, useReservations } from '@/hooks/data';
import { useFeatureAccess } from '@/components/plan/FeatureGate';
import { isRepeatEnabled } from '@/lib/repeatGate';

interface GlobalSearchCommandProps {
  showLauncher?: boolean;
}

const repeatCommands = [
  {
    label: '今週フォローすべき人を確認する',
    to: '/repeat?view=home',
  },
  {
    label: '離れそうなお客さま候補を確認する',
    to: '/repeat?view=queue&lane=churn',
  },
  {
    label: 'お客さまの文面候補を確認する',
    to: '/repeat?view=customers',
  },
  {
    label: '口コミ候補を確認する',
    to: '/repeat?view=queue&lane=follow',
  },
  {
    label: '周期フォロー設定を見る',
    to: '/repeat?view=setup',
  },
  {
    label: '成果の集計状況を見る',
    to: '/repeat?view=proof',
  },
  {
    label: 'LINE未連携のお客さまを確認する',
    to: '/repeat?view=customers',
  },
  {
    label: 'データの調子を見る',
    to: '/repeat?view=setup',
  },
] as const;

export function GlobalSearchCommand({
  showLauncher = false,
}: GlobalSearchCommandProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const hasRepeatAccess = useFeatureAccess('repeat');
  const isRepeatMode =
    location.pathname.startsWith('/repeat') &&
    isRepeatEnabled() &&
    hasRepeatAccess;

  // TanStack Query hooks for customers and reservations (Issue #1282)
  const { data: customers = [] } = useCustomers();
  const { data: reservations = [] } = useReservations();

  // Cmd+K / Ctrl+K でトグル
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // 検索結果フィルタリング
  const filteredCustomers = React.useMemo(() => {
    if (!query) return [];
    const searchLower = query.toLowerCase();
    return customers
      .filter((c) => {
        const fullName = `${c.lastName} ${c.firstName}`.toLowerCase();
        return (
          fullName.includes(searchLower) ||
          c.phoneNumber.includes(query) ||
          (c.email?.toLowerCase().includes(searchLower) ?? false)
        );
      })
      .slice(0, 5);
  }, [query, customers]);

  const filteredReservations = React.useMemo(() => {
    if (!query) return [];
    const searchLower = query.toLowerCase();
    return reservations
      .filter(
        (r) =>
          r.customerName?.toLowerCase().includes(searchLower) ||
          r.reservationNumber?.includes(query)
      )
      .slice(0, 5);
  }, [query, reservations]);

  const handleSelect = (callback: () => void) => {
    setOpen(false);
    setQuery('');
    callback();
  };

  const filteredRepeatCommands = React.useMemo(() => {
    if (!query) return repeatCommands;
    return repeatCommands.filter((command) => command.label.includes(query));
  }, [query]);

  return (
    <>
      {showLauncher && isRepeatMode && (
        <button
          type="button"
          aria-label="Ripiaに頼む… ⌘K"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-between gap-3 rounded-[8px] border border-white/18 bg-white px-3 py-2.5 text-left text-sm font-medium text-[#243326] shadow-sm transition hover:bg-[#fffaf2]"
        >
          <span className="flex min-w-0 items-center gap-2">
            <MessageCircle className="h-4 w-4 shrink-0 text-[#2f4f35]" />
            <span className="truncate">Ripiaに頼む…</span>
          </span>
          <span
            aria-hidden="true"
            className="flex shrink-0 items-center gap-1 text-xs text-[#6c766c]"
          >
            <span>/</span>
            <kbd className="rounded border border-[#d9cfbf] bg-[#f6f2ea] px-1.5 py-0.5 font-sans">
              ⌘K
            </kbd>
          </span>
        </button>
      )}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder={
            isRepeatMode
              ? 'リピート作成でRipiaに頼むことを選択'
              : '顧客、予約、商品を検索... (Ctrl+K)'
          }
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>結果が見つかりません。</CommandEmpty>

          {isRepeatMode && (
            <>
              <CommandGroup heading="Ask Ripia">
                {filteredRepeatCommands.map((command) => (
                  <CommandItem
                    key={command.label}
                    onSelect={() => handleSelect(() => navigate(command.to))}
                  >
                    <MessageCircle className="mr-2 h-4 w-4 text-[var(--brand)]" />
                    <span>{command.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* ナビゲーションショートカット (/repeat では Ask Ripia のみ表示するため非表示) */}
          {!query && !isRepeatMode && (
            <>
              <CommandGroup heading="ナビゲーション">
                <CommandItem
                  onSelect={() => handleSelect(() => navigate('/dashboard'))}
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  <span>ダッシュボード</span>
                  <CommandShortcut>⌘D</CommandShortcut>
                </CommandItem>
                <CommandItem
                  onSelect={() => handleSelect(() => navigate('/reservations'))}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>予約管理</span>
                  <CommandShortcut>⌘R</CommandShortcut>
                </CommandItem>
                <CommandItem
                  onSelect={() => handleSelect(() => navigate('/customers'))}
                >
                  <Users className="mr-2 h-4 w-4" />
                  <span>顧客管理</span>
                  <CommandShortcut>⌘C</CommandShortcut>
                </CommandItem>
                {isRepeatEnabled() && hasRepeatAccess && (
                  <CommandItem
                    onSelect={() => handleSelect(() => navigate('/repeat'))}
                  >
                    <HeartHandshake className="mr-2 h-4 w-4" />
                    <span>リピート作成</span>
                  </CommandItem>
                )}
                <CommandItem
                  onSelect={() => handleSelect(() => navigate('/staff'))}
                >
                  <UserCog className="mr-2 h-4 w-4" />
                  <span>スタッフ管理</span>
                  <CommandShortcut>⌘T</CommandShortcut>
                </CommandItem>
                <CommandItem
                  onSelect={() => handleSelect(() => navigate('/sales'))}
                >
                  <TrendingUp className="mr-2 h-4 w-4" />
                  <span>売上分析</span>
                  <CommandShortcut>⌘S</CommandShortcut>
                </CommandItem>
              </CommandGroup>

              <CommandSeparator />

              {/* Issue #1913: Organization廃止 - 店舗設定に名称変更 */}
              <CommandGroup heading="設定・ヘルプ">
                <CommandItem
                  onSelect={() =>
                    handleSelect(() => navigate('/settings?tab=shop'))
                  }
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  <span>店舗設定</span>
                </CommandItem>
                <CommandItem
                  onSelect={() => handleSelect(() => navigate('/settings'))}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  <span>設定</span>
                </CommandItem>
                <CommandItem
                  onSelect={() => handleSelect(() => navigate('/help'))}
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  <span>ヘルプ</span>
                </CommandItem>
              </CommandGroup>

              <CommandSeparator />

              <CommandGroup heading="AIアシスタント">
                <CommandItem
                  onSelect={() => handleSelect(() => navigate('/ai-chat'))}
                >
                  <Sparkles className="mr-2 h-4 w-4 text-indigo-600" />
                  <span>AI相談を開始</span>
                  <CommandShortcut>⌘A</CommandShortcut>
                </CommandItem>
              </CommandGroup>
            </>
          )}

          {/* 顧客検索結果 (/repeat では非表示) */}
          {query && !isRepeatMode && filteredCustomers.length > 0 && (
            <>
              <CommandGroup heading="顧客">
                {filteredCustomers.map((customer) => (
                  <CommandItem
                    key={customer.id}
                    onSelect={() =>
                      handleSelect(() => navigate(`/customers/${customer.id}`))
                    }
                  >
                    <Users className="mr-2 h-4 w-4" />
                    <span>
                      {customer.lastName} {customer.firstName}
                    </span>
                    <CommandShortcut>{customer.phoneNumber}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* 予約検索結果 (/repeat では非表示) */}
          {query && !isRepeatMode && filteredReservations.length > 0 && (
            <>
              <CommandGroup heading="予約">
                {filteredReservations.map((reservation) => (
                  <CommandItem
                    key={reservation.id}
                    onSelect={() =>
                      handleSelect(() =>
                        navigate(`/reservations/${reservation.id}`)
                      )
                    }
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    <span>
                      {reservation.customerName} -{' '}
                      {new Date(reservation.reservationDate).toLocaleDateString(
                        'ja-JP'
                      )}
                    </span>
                    <CommandShortcut>
                      {reservation.reservationNumber}
                    </CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* AI検索提案 (/repeat では非表示) */}
          {query &&
            !isRepeatMode &&
            (filteredCustomers.length > 0 ||
              filteredReservations.length > 0) && (
              <>
                <CommandSeparator />
                <CommandGroup heading="AI分析">
                  <CommandItem
                    onSelect={() =>
                      handleSelect(() =>
                        navigate(`/ai-chat?query=${encodeURIComponent(query)}`)
                      )
                    }
                  >
                    <Sparkles className="mr-2 h-4 w-4 text-indigo-600" />
                    <span>「{query}」についてAIに相談</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
